import React, { useEffect, useRef, useState } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import iconBell from '../assets/icon-bell.svg';
import './NotificationBell.css';

const formatTimestamp = (timestamp) => {
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Unable to format notification timestamp:', error);
    return '';
  }
};

const NotificationBell = () => {
  const {
    notifications,
    unreadCount,
    refreshNotifications,
    markAllAsRead,
    removeNotification,
    clearNotifications
  } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef(null);
  const popoverRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!isOpen) {
        return;
      }

      const toggleEl = toggleRef.current;
      const popoverEl = popoverRef.current;
      if (
        toggleEl &&
        popoverEl &&
        !toggleEl.contains(event.target) &&
        !popoverEl.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      refreshNotifications();
    } else if (wasOpenRef.current && unreadCount > 0) {
      markAllAsRead();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, unreadCount, refreshNotifications, markAllAsRead]);

  const handleToggle = () => {
    setIsOpen((previous) => !previous);
  };

  return (
    <div className="notification-bell">
      <button
        type="button"
        className="icon-button notification-button"
        ref={toggleRef}
        onClick={handleToggle}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={unreadCount ? `${unreadCount} unread notifications` : 'Notifications'}
      >
        <img src={iconBell} alt="" aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="notification-badge">{Math.min(unreadCount, 99)}</span>
        )}
      </button>
      {isOpen && (
        <div className="notification-popover" ref={popoverRef} role="dialog" aria-label="Notifications">
          <div className="notification-popover__header">
            <span>Notifications</span>
            {notifications.length > 0 && (
              <button
                type="button"
                className="notification-popover__clear"
                onClick={clearNotifications}
              >
                Clear all
              </button>
            )}
          </div>
          <ul className="notification-popover__list">
            {notifications.length === 0 && (
              <li className="notification-popover__empty">You're all caught up!</li>
            )}
            {notifications.map((notification) => (
              <li key={notification.id} className="notification-popover__item">
                <div className="notification-popover__copy">
                  <p className="notification-popover__message">{notification.message}</p>
                  {notification.created_at && (
                    <span className="notification-popover__timestamp">
                      {formatTimestamp(notification.created_at)}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="notification-popover__dismiss"
                  onClick={() => removeNotification(notification.id)}
                  aria-label="Dismiss notification"
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
